import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Infinity as InfinityIcon, ShieldCheck, Copy, Check, RotateCcw } from "lucide-react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";

export default function SealProofDialog({ open, onOpenChange, card, onSealed }) {
  const [loading, setLoading] = useState(false);
  const [proof, setProof] = useState(null);
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setProof(null);
    setCopied(false);
  };

  const handleSeal = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/cards/${card.id}/seal-reset`);
      setProof(res.data.proof);
      onSealed();
      toast.success("Signature sealed as immortal network proof");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nothing to seal");
    } finally {
      setLoading(false);
    }
  };

  const copySeal = () => {
    navigator.clipboard.writeText(proof.proof_seal);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const fmt = (s) => s.match(/.{1,4}/g)?.join(" ") || s;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="bg-[#121216] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display font-black tracking-tight flex items-center gap-2">
            <InfinityIcon className="w-5 h-5 text-[#FFE600]" /> Seal &amp; Reset
          </DialogTitle>
          <DialogDescription className="text-[#A0A0AB]">
            Seal this card's last signature as an immortal network proof (cardholder hidden), then
            delete it and reset the nonce back to zero.
          </DialogDescription>
        </DialogHeader>

        {!proof ? (
          <div className="space-y-4 pt-2">
            <div className="rounded-xl border border-[#FFE600]/25 bg-[#FFE600]/5 p-4">
              <p className="text-sm text-[#A0A0AB] leading-relaxed">
                This action archives the most recent signed payment as a permanent, tamper-evident
                proof and resets the signature counter. It cannot be undone.
              </p>
            </div>
            <Button
              data-testid="seal-confirm-button"
              onClick={handleSeal}
              disabled={loading}
              className="w-full h-12 bg-[#FFE600] text-black hover:bg-[#FFE600]/90 font-semibold rounded-xl"
            >
              <RotateCcw className="w-4 h-4 mr-2" /> {loading ? "Sealing…" : "Seal last & reset to zero"}
            </Button>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4 pt-2">
            <div
              data-testid="immortal-proof"
              className="relative rounded-2xl overflow-hidden border border-[#FFE600]/40 p-5"
              style={{ background: "linear-gradient(135deg,#1a1a20,#0f0f12)" }}
            >
              <div className="pointer-events-none absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#FFE600] opacity-[0.10] blur-2xl" />
              <div className="flex items-center justify-between mb-4">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[#FFE600]/40 bg-[#FFE600]/10 px-3 py-1">
                  <InfinityIcon className="w-3.5 h-3.5 text-[#FFE600]" />
                  <span className="font-mono-sig text-[10px] tracking-[0.3em] uppercase text-[#FFE600]">Immortal</span>
                </span>
                <span className="font-mono-sig text-[10px] tracking-[0.2em] uppercase text-[#A0A0AB]">{proof.network} Proof</span>
              </div>

              <p className="font-mono-sig text-[9px] tracking-[0.3em] uppercase text-[#52525B] mb-1">Cardholder</p>
              <p data-testid="proof-cardholder" className="font-mono-sig text-sm text-[#A0A0AB] mb-4">{proof.cardholder}</p>

              <p className="font-mono-sig text-[9px] tracking-[0.3em] uppercase text-[#52525B] mb-1">Sealed Signature · Nonce #{proof.nonce}</p>
              <p className="font-mono-sig text-sm tracking-[0.12em] text-[#00F0FF] break-all mb-4">{fmt(proof.signature)}</p>

              <div className="flex items-center justify-between text-sm border-t border-white/10 pt-3">
                <span className="text-[#A0A0AB]">{proof.merchant}</span>
                <span className="font-mono-sig font-semibold">${proof.amount.toFixed(2)}</span>
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <ShieldCheck className="w-3.5 h-3.5 text-[#00FF9D]" />
                <p className="font-mono-sig text-[9px] tracking-[0.3em] uppercase text-[#A0A0AB]">Network Seal (SHA-256)</p>
              </div>
              <p data-testid="proof-seal" className="font-mono-sig text-[11px] tracking-[0.1em] text-[#00FF9D] break-all">{proof.proof_seal}</p>
            </div>

            <div className="flex gap-2">
              <Button
                data-testid="copy-seal-button"
                onClick={copySeal}
                variant="outline"
                className="flex-1 h-11 border-white/10 bg-white/5 text-white hover:bg-white/10 rounded-xl"
              >
                {copied ? <Check className="w-4 h-4 mr-2 text-[#00FF9D]" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? "Copied" : "Copy seal"}
              </Button>
              <Button
                data-testid="proof-done-button"
                onClick={() => { reset(); onOpenChange(false); }}
                className="flex-1 h-11 bg-white text-black hover:bg-white/90 rounded-xl font-semibold"
              >
                Done
              </Button>
            </div>
          </motion.div>
        )}
      </DialogContent>
    </Dialog>
  );
}
